/**
 * Auth redirect URL utilities (aligned with mf-platform / mf-auth-management).
 */

const REDIRECT_URL_KEY = 'auth_redirect_url';

export const SSO_REDIRECT_TOKEN_PARAM = 'sso__redirect__token';
export const SSO_REDIRECT_SCOPE_PARAM = 'sso__redirect__scope';

const excludedPaths = [
  '/login',
  '/logout',
  '/callback',
  '/consent',
  '/sso-callback',
  '/unauthorized',
  '/session-expired',
  '/sso/oidc/callback',
  '/sso/saml/acs',
  '/auth/saml/acs',
];

export type SsoRedirectScope = 'tenant' | 'account';

export interface SsoRedirectContext {
  token: string;
  scope: SsoRedirectScope;
}

const stripSsoRedirectParamsFromSearchParams = (
  searchParams: URLSearchParams,
): void => {
  searchParams.delete(SSO_REDIRECT_TOKEN_PARAM);
  searchParams.delete(SSO_REDIRECT_SCOPE_PARAM);
};

export const storeRedirectUrl = (url?: string): void => {
  const urlToStore =
    url || `${window.location.pathname}${window.location.search}`;
  const sanitizedUrl = sanitizeRedirectUrlForStorage(urlToStore);

  const shouldStore = !excludedPaths.some(path => sanitizedUrl.includes(path));

  if (shouldStore && sanitizedUrl !== '/') {
    sessionStorage.setItem(REDIRECT_URL_KEY, sanitizedUrl);
  }
};

const sanitizeRedirectUrlForStorage = (url: string): string => {
  if (typeof window === 'undefined') {
    return url;
  }

  try {
    const redirectUrl = new URL(url, window.location.origin);
    stripSsoRedirectParamsFromSearchParams(redirectUrl.searchParams);
    const sanitizedSearch = redirectUrl.searchParams.toString();
    return `${redirectUrl.pathname}${sanitizedSearch ? `?${sanitizedSearch}` : ''}`;
  } catch {
    return url;
  }
};

export const getSsoRedirectContextFromUrl = (
  search?: string,
): SsoRedirectContext | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const query = search ?? window.location.search;
  const searchParams = new URLSearchParams(query);
  const token = searchParams.get(SSO_REDIRECT_TOKEN_PARAM)?.trim();
  const scope = searchParams.get(SSO_REDIRECT_SCOPE_PARAM)?.trim();

  if (!token || token.includes('/')) {
    return null;
  }

  if (scope !== 'tenant' && scope !== 'account') {
    return null;
  }

  return { token, scope };
};

export const getEncryptedSsoLoginPathFromSession = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const savedEncryptedTenantId = sessionStorage
    .getItem('encrypted_tenant_id')
    ?.trim();
  const savedScope = sessionStorage.getItem('encrypted_scope')?.trim();

  if (!savedEncryptedTenantId || savedEncryptedTenantId.includes('/')) {
    return null;
  }

  if (savedScope !== 'tenant' && savedScope !== 'account') {
    return null;
  }

  return `/${savedEncryptedTenantId}/${savedScope}/login`;
};

export const getLoginRedirectPath = (search?: string): string => {
  const ssoRedirectContext = getSsoRedirectContextFromUrl(search);
  if (ssoRedirectContext) {
    return `/${ssoRedirectContext.token}/${ssoRedirectContext.scope}/login`;
  }

  const sessionSsoLoginPath = getEncryptedSsoLoginPathFromSession();
  if (sessionSsoLoginPath) {
    return sessionSsoLoginPath;
  }

  return '/login';
};

export const getLoginRedirectUrl = (
  baseUrl: string,
  search?: string,
): string => {
  const strippedBase = baseUrl.replace(/\/$/, '');
  return `${strippedBase}${getLoginRedirectPath(search)}`;
};
